import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Clock, MapPin, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import { menuTranslations } from '@/i18n/pickupMenu';
import { storeApi } from '@/lib/api/stores';
import { logger } from '@/utils/logger';
import { getStoreOpenLabel } from '@/lib/storeOpenLabel';
import type { Locale } from '@/config/locales';
import type { StoreHoursResponse, StoreOpenCheckResponse, StoreHoursEntry, StoreHoursSegment, EntityStatus, StoreClosureReason } from '@/lib/api/types';

interface HoursModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: number;
  storeName: string;
  storeAddress?: string;
  /**
   * Q7 박제 옵션 C — admin#1230 / admin#1298 / menu#347.
   *
   * 아래 필드 모두 제공 시 BE 응답 기반 라벨 매트릭스를 사용하며,
   * `/hours/check` 의 `is_open` 단일 boolean 분기는 무시됩니다.
   * raw `operating_hours` 재해석 금지 박제 정합.
   */
  storeStatus?: EntityStatus;
  storeIsOpenNow?: boolean;
  storeClosureReason?: StoreClosureReason | null;
  storeNextAvailableAt?: string | null;
  storeTimezone?: string | null;
  /**
   * Q7 박제 옵션 C — BE 가 `hours` relation 을 로드해 closure_reason /
   * next_available_at 을 함께 응답했는지 여부 (P1-1 회귀 게이트).
   *
   * - `true`: Q7 4 필드 매트릭스 신뢰 가능 — 라벨 매트릭스 적용
   * - `false`/`undefined`: hours relation 미로드 — 라벨 매트릭스 미적용,
   *   `/hours/check` 기반 폴백 (silent regression 차단).
   */
  storeHasQ7Hours?: boolean;
}

interface HoursModalTranslation {
  operatingHours: string;
  location: string;
  closed: string;
  currentlyOpen: string;
  currentlyClosed: string;
  loadError: string;
}

const translations = {
  ko: {
    operatingHours: '영업 시간',
    location: '위치',
    closed: '휴무',
    currentlyOpen: '영업 중',
    currentlyClosed: '영업 종료',
    loadError: '영업시간 정보를 불러올 수 없습니다.',
  },
  es: {
    operatingHours: 'Horarios de Operación',
    location: 'Ubicación',
    closed: 'Cerrado',
    currentlyOpen: 'Abierto ahora',
    currentlyClosed: 'Cerrado ahora',
    loadError: 'No se pudo cargar la información de horarios.',
  },
  en: {
    operatingHours: 'Operating Hours',
    location: 'Location',
    closed: 'Closed',
    currentlyOpen: 'Currently Open',
    currentlyClosed: 'Currently Closed',
    loadError: 'Could not load hours information.',
  },
  pt: {
    operatingHours: 'Horário de funcionamento',
    location: 'Localização',
    closed: 'Fechado',
    currentlyOpen: 'Aberto agora',
    currentlyClosed: 'Fechado agora',
    loadError: 'Não foi possível carregar as informações de horário.',
  },
  fr: {
    operatingHours: 'Horaires d\'ouverture',
    location: 'Emplacement',
    closed: 'Fermé',
    currentlyOpen: 'Ouvert maintenant',
    currentlyClosed: 'Fermé maintenant',
    loadError: 'Impossible de charger les horaires.',
  },
  zh: {
    operatingHours: '营业时间',
    location: '位置',
    closed: '休息',
    currentlyOpen: '当前营业',
    currentlyClosed: '当前关闭',
    loadError: '无法加载营业时间信息。',
  },
  ja: {
    operatingHours: '営業時間',
    location: '位置',
    closed: '休業',
    currentlyOpen: '営業中',
    currentlyClosed: '営業終了',
    loadError: '営業時間情報を読み込めませんでした。',
  },
  hi: {
    operatingHours: 'संचालन समय',
    location: 'स्थान',
    closed: 'बंद',
    currentlyOpen: 'अभी खुला है',
    currentlyClosed: 'अभी बंद है',
    loadError: 'समय जानकारी लोड नहीं हो सकी।',
  },
  de: {
    operatingHours: 'Öffnungszeiten',
    location: 'Standort',
    closed: 'Geschlossen',
    currentlyOpen: 'Jetzt geöffnet',
    currentlyClosed: 'Jetzt geschlossen',
    loadError: 'Öffnungszeiten konnten nicht geladen werden.',
  },
  it: {
    operatingHours: 'Orari di apertura',
    location: 'Posizione',
    closed: 'Chiuso',
    currentlyOpen: 'Aperto ora',
    currentlyClosed: 'Chiuso ora',
    loadError: 'Impossibile caricare gli orari.',
  },
  ru: {
    operatingHours: 'Часы работы',
    location: 'Местоположение',
    closed: 'Закрыто',
    currentlyOpen: 'Открыто',
    currentlyClosed: 'Закрыто',
    loadError: 'Не удалось загрузить часы работы.',
  },
  ar: {
    operatingHours: 'ساعات العمل',
    location: 'الموقع',
    closed: 'مغلق',
    currentlyOpen: 'مفتوح الآن',
    currentlyClosed: 'مغلق الآن',
    loadError: 'تعذر تحميل معلومات ساعات العمل.',
  },
  vi: {
    operatingHours: 'Giờ hoạt động',
    location: 'Vị trí',
    closed: 'Đóng cửa',
    currentlyOpen: 'Đang mở',
    currentlyClosed: 'Đang đóng',
    loadError: 'Không thể tải thông tin giờ hoạt động.',
  },
  th: {
    operatingHours: 'เวลาเปิดทำการ',
    location: 'ตำแหน่ง',
    closed: 'ปิด',
    currentlyOpen: 'เปิดอยู่ตอนนี้',
    currentlyClosed: 'ปิดอยู่ตอนนี้',
    loadError: 'ไม่สามารถโหลดข้อมูลเวลาเปิดทำการได้',
  },
  nl: {
    operatingHours: 'Openingstijden',
    location: 'Locatie',
    closed: 'Gesloten',
    currentlyOpen: 'Nu open',
    currentlyClosed: 'Nu gesloten',
    loadError: 'Kan openingstijden niet laden.',
  },
} satisfies Record<Locale, HoursModalTranslation>;

function getHoursModalTranslation(locale: Locale): HoursModalTranslation {
  return translations[locale];
}

/** segments → 표시 문자열 */
function formatSegments(segments: StoreHoursSegment[], formatTime: (t: string) => string): string {
  if (segments.length === 0) return '';
  return segments.map((s) => `${formatTime(s.open)} - ${formatTime(s.close)}`).join(' / ');
}

function getLocalizedDayName(entry: StoreHoursEntry, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(entry.date);
  if (!match) return entry.day_name;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return entry.day_name;
  }

  return menuTranslations[locale].days[date.getUTCDay()] ?? entry.day_name;
}

/**
 * 매장 timezone 기준 YYYY-MM-DD 문자열 생성.
 *
 * #1494 R1 codex P1 시정:
 * - locale 'en-CA' 의 format() 결과는 ECMA 계약상 locale-sensitive 문자열.
 *   YYYY-MM-DD 고정 형식 보장 X → formatToParts() 로 명시적 조립.
 * - Invalid IANA timezone 입력 시 RangeError throw 되어 modal crash → try/catch
 *   로 브라우저 timezone fallback (silent failure 가 아닌 graceful degradation).
 */
function formatYmdInTimezone(date: Date, timezone: string | null | undefined): string {
  const tryFormat = (tz: string): string => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type: 'year' | 'month' | 'day'): string | undefined =>
      parts.find((p) => p.type === type)?.value;
    const year = get('year');
    const month = get('month');
    const day = get('day');
    // R2 codex P1 시정: part 누락 시 malformed "YYYY-MM-DD" 조립 차단.
    if (!year || !month || !day) {
      throw new Error(`formatToParts 누락 — tz=${tz}`);
    }
    return `${year}-${month}-${day}`;
  };

  if (timezone) {
    try {
      return tryFormat(timezone);
    } catch (err) {
      // R2 codex P1 시정: invalid IANA timezone (RangeError) 만 fallback.
      // 다른 예외는 silent failure 차단 위해 rethrow.
      if (!(err instanceof RangeError)) {
        throw err;
      }
    }
  }
  return tryFormat(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * 오늘 날짜 기준 7일 범위 계산 (매장 timezone 반영).
 *
 * #1494 R1 양 채널 P1 시정:
 * - 기존 `toISOString()` 은 UTC 기준 → 매장 timezone 기준 변환
 * - end_date 의 `+ 6 * 24h` 고정 ms 덧셈 → DST 전환일 시 로컬 +6일 어긋남.
 *   calendar-day add (Date setDate) 로 DST-safe 계산.
 */
function getDateRange(timezone?: string | null): { start_date: string; end_date: string } {
  const now = new Date();
  const start = formatYmdInTimezone(now, timezone);

  // R2 양 채널 P1 시정: start 문자열 자체에 UTC calendar day +6 적용.
  // 이전 UTC noon 앵커 + ms 덧셈 + timezone 재변환 방식은 UTC+13/+14 매장 (e.g.
  // Pacific/Kiritimati) 에서 noon 이 현지 익일로 넘어가 end_date +1일 어긋남.
  // Date.UTC(y, m-1, d+6) 는 month overflow 자동 정규화 + UTC offset / DST 모두
  // 무관 (start 문자열 day 단위 산술만 사용).
  const [y, m, d] = start.split('-').map(Number);
  const endUtc = new Date(Date.UTC(y, m - 1, d + 6));
  const end = `${endUtc.getUTCFullYear()}-${String(endUtc.getUTCMonth() + 1).padStart(2, '0')}-${String(endUtc.getUTCDate()).padStart(2, '0')}`;

  return { start_date: start, end_date: end };
}

export function HoursModal({
  isOpen,
  onClose,
  storeId,
  storeName,
  storeAddress,
  storeStatus,
  storeIsOpenNow,
  storeClosureReason,
  storeNextAvailableAt,
  storeTimezone,
  storeHasQ7Hours,
}: HoursModalProps) {
  const { locale } = useLanguage();
  const t = getHoursModalTranslation(locale);

  /**
   * Q7 박제 옵션 C — BE 4 필드 + hours relation 로드 시에만 라벨 매트릭스 사용.
   *
   * P1-1 회귀 게이트 (codex 1라운드 학습):
   * - status / is_open_now 는 매장 목록에서도 항상 응답되지만,
   *   closure_reason / next_available_at 는 hours relation 로드 시에만 응답됨.
   * - relation 미로드 응답을 매트릭스에 통과시키면 outside_hours 분기가
   *   silently 누락 → "영업종료" 로 폴백되어 silent regression.
   * - storeHasQ7Hours=true 일 때만 매트릭스 적용. 그 외 `/hours/check` 폴백.
   */
  const hasQ7Payload =
    storeStatus !== undefined &&
    storeIsOpenNow !== undefined &&
    storeHasQ7Hours === true;
  const q7Label = hasQ7Payload
    ? getStoreOpenLabel(
        {
          status: storeStatus,
          isOpenNow: storeIsOpenNow,
          closureReason: storeClosureReason ?? null,
          nextAvailableAt: storeNextAvailableAt ?? null,
          storeTimezone: storeTimezone ?? null,
        },
        locale,
      )
    : null;

  const [hoursData, setHoursData] = useState<StoreHoursResponse | null>(null);
  const [openStatus, setOpenStatus] = useState<StoreOpenCheckResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !storeId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const dateRange = getDateRange(storeTimezone);

    // 두 API가 독립적이므로 allSettled로 부분 성공 허용
    Promise.allSettled([
      storeApi.getStoreHours(storeId, dateRange),
      storeApi.checkStoreOpen(storeId),
    ])
      .then(([hoursResult, checkResult]) => {
        if (cancelled) return;

        if (hoursResult.status === 'fulfilled') {
          setHoursData(hoursResult.value);
        } else {
          logger.warn('[HoursModal] 영업시간 조회 실패:', hoursResult.reason);
        }

        if (checkResult.status === 'fulfilled') {
          setOpenStatus(checkResult.value);
        } else {
          logger.warn('[HoursModal] 영업 상태 확인 실패:', checkResult.reason);
        }

        // 둘 다 실패한 경우에만 에러 표시
        if (hoursResult.status === 'rejected' && checkResult.status === 'rejected') {
          setError(getHoursModalTranslation(locale).loadError);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [storeId, isOpen, locale, storeTimezone]);

  const formatTime = (time: string) => {
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour);

    if (locale === 'en') {
      const period = hourNum >= 12 ? 'PM' : 'AM';
      const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      return `${displayHour}:${minute} ${period}`;
    }

    return `${hour}:${minute}`;
  };

  /** 7일 데이터를 요일별로 그룹핑 (첫 번째 해당 요일 사용) */
  const groupedByDay = (() => {
    if (!hoursData?.hours) return [];
    const seen = new Set<string>();
    const result: StoreHoursEntry[] = [];
    for (const entry of hoursData.hours) {
      const key = entry.date || entry.day_name;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(entry);
      }
    }
    return result;
  })();

  /**
   * 오늘의 날짜(YYYY-MM-DD) — #1494 시정: 매장 timezone 기준.
   * UTC 기준 toISOString() 사용 시 BE 응답 day_name 의 isToday 매칭이
   * 사용자 timezone 자정~UTC 자정 구간에 어긋나는 silent failure 차단.
   * R1 codex P1 시정 정합 — formatToParts + invalid tz fallback 적용.
   */
  const todayDate = formatYmdInTimezone(new Date(), storeTimezone);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-background border-none rounded-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[clamp(16px,4vw,20px)] font-bold text-foreground font-['Noto_Sans',sans-serif] flex items-center gap-2">
            <Clock className="h-[clamp(18px,4.5vw,22px)] w-[clamp(18px,4.5vw,22px)] text-primary" />
            {t.operatingHours}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-[clamp(20px,5vw,24px)] py-4">
          {/* 매장 이름 + 영업 상태 */}
          <div className="border-b border-border pb-[clamp(16px,4vw,20px)]">
            <h3 className="text-[clamp(16px,4vw,18px)] font-medium text-foreground font-['Noto_Sans',sans-serif] mb-2">
              {storeName}
            </h3>
            {q7Label ? (
              // Q7 박제 옵션 C — BE 응답 기반 라벨 (raw 영업시간 재해석 금지)
              <div
                className={`inline-flex items-center gap-2 px-[clamp(8px,2vw,12px)] py-[clamp(4px,1vw,6px)] rounded-full text-[clamp(12px,3vw,14px)] font-medium ${
                  q7Label.tone === 'primary'
                    ? 'bg-primary/10 text-primary'
                    : q7Label.tone === 'warning'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-destructive/10 text-destructive'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    q7Label.tone === 'primary'
                      ? 'bg-primary'
                      : q7Label.tone === 'warning'
                        ? 'bg-amber-500'
                        : 'bg-destructive'
                  }`}
                />
                {q7Label.text}
              </div>
            ) : isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : openStatus && (
              // 폴백 — Q7 4 필드 미제공 시 (구버전 호출자) 기존 /hours/check 사용
              <div
                className={`inline-flex items-center gap-2 px-[clamp(8px,2vw,12px)] py-[clamp(4px,1vw,6px)] rounded-full text-[clamp(12px,3vw,14px)] font-medium ${
                  openStatus.is_open
                    ? 'bg-primary/10 text-primary'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${openStatus.is_open ? 'bg-primary' : 'bg-destructive'}`}
                />
                {openStatus.is_open ? t.currentlyOpen : t.currentlyClosed}
              </div>
            )}
          </div>

          {/* 영업시간 목록 */}
          {!isLoading && !error && (
            <div>
              <h4 className="text-[clamp(14px,3.5vw,16px)] font-medium text-foreground font-['Noto_Sans',sans-serif] mb-[clamp(12px,3vw,16px)]">
                {t.operatingHours}
              </h4>
              <div className="space-y-[clamp(8px,2vw,12px)]">
                {groupedByDay.map((entry) => {
                  const isToday = entry.date === todayDate;
                  const dayName = getLocalizedDayName(entry, locale);

                  return (
                    <div
                      key={entry.date}
                      className={`p-[clamp(12px,3vw,16px)] rounded-[5px] transition-colors duration-200 ${
                        isToday
                          ? 'bg-primary/10 border border-primary'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-[clamp(13px,3.2vw,15px)] font-medium font-['Noto_Sans',sans-serif] ${
                              isToday ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {dayName}
                          </span>
                          {/* holiday/custom 라벨 */}
                          {entry.label && (entry.type === 'holiday' || entry.type === 'custom') && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              {entry.label}
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-[clamp(13px,3.2vw,15px)] font-medium font-['Noto_Sans',sans-serif] ${
                            entry.is_closed
                              ? 'text-muted-foreground'
                              : isToday
                                ? 'text-primary'
                                : 'text-foreground'
                          }`}
                        >
                          {entry.is_closed
                            ? t.closed
                            : formatSegments(entry.segments, formatTime)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 위치 */}
          {storeAddress && (
            <div>
              <h4 className="text-[clamp(14px,3.5vw,16px)] font-medium text-foreground font-['Noto_Sans',sans-serif] mb-[clamp(12px,3vw,16px)] flex items-center gap-2">
                <MapPin className="h-[clamp(16px,4vw,18px)] w-[clamp(16px,4vw,18px)] text-primary" />
                {t.location}
              </h4>
              <div className="bg-muted p-[clamp(12px,3vw,16px)] rounded-xl">
                <p className="text-[clamp(13px,3.2vw,15px)] text-foreground font-['Noto_Sans',sans-serif] leading-relaxed">
                  {storeAddress}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
